import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { formatApiError } from '@/lib/formatApiError';
import { formatCount, formatCtr, formatDuration, formatWatch, rangeQuery, youtubeThumbnail } from '@/lib/channel';
import { SeoScoreRing } from '@/components/channel/SeoScoreRing';
import { ChannelSectionNav } from '@/components/channel/ChannelSectionNav';
import { RangeSwitcher } from '@/components/channel/RangeSwitcher';
import { SeoChecklist } from '@/components/channel/SeoChecklist';

const fieldClass = 'mt-2 w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-[#34d399]/40';

function shown(value, formatted) {
  return Number(value) > 0 ? formatted : '—';
}

export function ChannelVideoDetailPage() {
  const { videoId } = useParams();
  const [preset, setPreset] = useState('28d');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [privacy, setPrivacy] = useState('public');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [language, setLanguage] = useState('');
  const [improving, setImproving] = useState(false);
  const [draftScore, setDraftScore] = useState(null);
  const formVideoId = useRef('');
  const [searchParams, setSearchParams] = useSearchParams();
  const autoImprove = useRef(false);

  const load = useCallback(async () => {
    if (preset === 'custom' && (!start || !end)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/channel/videos/${videoId}`, {
        params: rangeQuery(preset, start, end),
      });
      setPayload(data);
      if (formVideoId.current !== videoId) {
        const video = data?.video || {};
        setTitle(video.title || '');
        setDescription(video.description || '');
        setTags((video.tags || []).join(', '));
        setPrivacy(video.privacy_status || 'public');
        setThumbnailUrl(video.thumbnail_url || '');
        setLanguage(video.default_language || '');
        setDraftScore(null);
        formVideoId.current = videoId;
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load this video'));
    } finally {
      setLoading(false);
    }
  }, [videoId, preset, start, end]);

  const clearChannel = useCallback(() => {
    setPayload(null);
    setTitle('');
    setDescription('');
    setTags('');
    setThumbnailUrl('');
    setLanguage('');
    setDraftScore(null);
  }, []);
  useYouTubeAccountReset(clearChannel);

  const improveSeo = useCallback(async () => {
    setImproving(true);
    try {
      const { data } = await api.post(`/channel/videos/${videoId}/improve-seo`);
      if (data?.title) setTitle(data.title);
      if (typeof data?.description === 'string') setDescription(data.description);
      if (Array.isArray(data?.tags)) setTags(data.tags.join(', '));
      if (data?.default_language) setLanguage(data.default_language);
      if (Number.isFinite(Number(data?.score))) setDraftScore(Number(data.score));
      if (data?.error) toast.error(data.error);
      else toast.success(data?.strategy || 'Draft is in the form. Save to publish it.');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not rewrite SEO'));
    } finally {
      setImproving(false);
    }
  }, [videoId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (autoImprove.current || searchParams.get('improve') !== '1' || loading || !payload?.video) return;
    autoImprove.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('improve');
    setSearchParams(next, { replace: true });
    improveSeo();
  }, [searchParams, setSearchParams, improveSeo, loading, payload]);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/channel/videos/${videoId}`, {
        title: title.trim(),
        description,
        tags: tags.split(/[,#]+/).map((tag) => tag.trim()).filter(Boolean),
        privacy_status: privacy,
        category_id: payload?.video?.category_id || null,
        default_language: language || null,
        thumbnail_url: thumbnailUrl.trim() || null,
      });
      toast.success('Saved to YouTube');
      formVideoId.current = '';
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not update the video'));
    } finally {
      setSaving(false);
    }
  };

  const video = payload?.video;
  const totals = payload?.totals || {};
  const series = payload?.series || [];
  const hasViews = series.some((row) => Number(row.views) > 0);
  const retention = (payload?.retention || []).map((row) => ({
    date: `${Math.round((row.elapsed_ratio || 0) * 100)}%`,
    views: Math.round((row.audience_watch_ratio || 0) * 100),
  }));
  const traffic = payload?.traffic_sources || [];
  const thumb = thumbnailUrl.trim() || youtubeThumbnail(video);
  const published = String(video?.published_at || '').slice(0, 10);

  return (
    <main className="p-4 sm:p-8 space-y-5 font-jakarta overflow-x-hidden">
      <header className="space-y-4">
        <Link to="/channel/videos" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#8b95a7] hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" />
          All videos
        </Link>
        <div className="flex flex-col sm:flex-row gap-5 sm:items-end">
          {thumb ? (
            <img src={thumb} alt="" className="w-full sm:w-72 aspect-video rounded-[22px] object-cover bg-[#12151d]" />
          ) : (
            <div className="w-full sm:w-72 aspect-video rounded-[22px] bg-white/[0.04]" />
          )}
          <div className="min-w-0 flex-1 space-y-3">
            <p className="text-[11px] uppercase tracking-wide text-[#5eead4]">YouTube</p>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
              {video?.title || (loading ? 'Loading…' : 'No channel connected')}
            </h1>
            {video ? (
              <p className="text-sm text-[#8b95a7]">
                <span className="capitalize">{video.privacy_status || 'public'}</span>
                {' · '}
                {video.is_short ? 'Short' : 'Video'}
                {published ? ` · ${published}` : ''}
                {videoId ? (
                  <>
                    {' · '}
                    <a
                      href={`https://www.youtube.com/watch?v=${videoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#34d399] hover:text-white"
                    >
                      Watch <ExternalLink className="w-3 h-3" />
                    </a>
                  </>
                ) : null}
              </p>
            ) : null}
          </div>
        </div>
        <RangeSwitcher
          preset={preset}
          start={start}
          end={end}
          onChange={(next) => {
            setPreset(next.preset);
            setStart(next.start || '');
            setEnd(next.end || '');
          }}
        />
        <ChannelSectionNav />
      </header>

      {loading && !payload ? <div className="h-40 rounded-[28px] bg-white/[0.03] animate-pulse" /> : null}

      {video ? (
        <>
          <section className="rounded-[24px] bg-gradient-to-br from-[#17352e] via-[#12181f] to-[#10141c] px-5 py-5 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-[#9dbeb4]">Lifetime views</p>
                <p className="mt-1 text-4xl font-semibold tracking-tight text-white">
                  {formatCount(payload?.lifetime?.views)}
                </p>
              </div>
              <SeoScoreRing
                score={draftScore ?? video.seo_score}
                size={72}
                onImprove={improveSeo}
                improving={improving}
              />
            </div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-4">
              {[
                ['Likes', formatCount(payload?.lifetime?.likes)],
                ['Watch time', shown(totals.watch_minutes, formatWatch(totals.watch_minutes))],
                ['Avg view', shown(totals.average_view_duration_seconds, formatDuration(totals.average_view_duration_seconds))],
                ['Views in range', shown(totals.views, formatCount(totals.views))],
                ['Impressions', shown(totals.impressions, formatCount(totals.impressions))],
                ['CTR', shown(totals.ctr, formatCtr(totals.ctr))],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-[11px] uppercase tracking-wide text-[#7f8b9c]">{label}</p>
                  <p className="mt-1 text-lg font-semibold text-white">{value}</p>
                </div>
              ))}
            </div>
          </section>

          {hasViews ? (
            <section>
              <p className="text-sm font-medium text-white mb-3">Views over time</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="videoViewsFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={0.38} />
                        <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tickFormatter={(value) => String(value || '').slice(5)} tick={{ fill: '#7d8799', fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={28} />
                    <YAxis tick={{ fill: '#7d8799', fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
                    <Tooltip contentStyle={{ background: '#12151d', border: '1px solid #243044', borderRadius: 14, fontSize: 12 }} labelStyle={{ color: '#d5dbe6' }} />
                    <Area type="monotone" dataKey="views" stroke="#34d399" strokeWidth={2.5} fill="url(#videoViewsFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>
          ) : (
            <p className="text-sm text-[#7d8799]">No daily views in this range yet.</p>
          )}

          {retention.length > 0 || traffic.length > 0 ? (
            <section className="grid lg:grid-cols-2 gap-8">
              {retention.length > 0 ? (
                <div>
                  <p className="text-sm font-medium text-white mb-3">Audience retention</p>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={retention} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <XAxis dataKey="date" tick={{ fill: '#7d8799', fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fill: '#7d8799', fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
                        <Tooltip contentStyle={{ background: '#12151d', border: '1px solid #243044', borderRadius: 14, fontSize: 12 }} />
                        <Area type="monotone" dataKey="views" stroke="#5eead4" strokeWidth={2} fill="#5eead4" fillOpacity={0.12} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : null}
              {traffic.length > 0 ? (
                <div>
                  <p className="text-sm font-medium text-white mb-3">Traffic sources</p>
                  <ul className="space-y-1">
                    {traffic.map((row) => (
                      <li key={row.source} className="flex items-center justify-between gap-3 rounded-2xl px-2 py-2 text-sm">
                        <span className="text-white capitalize">{String(row.source || '').split('_').join(' ')}</span>
                        <span className="text-[#8b95a7]">{formatCount(row.views)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>
          ) : null}

          <form onSubmit={save} className="space-y-5">
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-white">Edit on YouTube</h2>
              <label className="block text-xs text-[#8b95a7]">
                Title
                <input value={title} maxLength={100} onChange={(event) => setTitle(event.target.value)} className={fieldClass} />
              </label>
              <label className="block text-xs text-[#8b95a7]">
                Description
                <textarea value={description} rows={8} maxLength={5000} onChange={(event) => setDescription(event.target.value)} className={`${fieldClass} resize-y`} />
              </label>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block text-xs text-[#8b95a7]">
                  Tags
                  <input value={tags} onChange={(event) => setTags(event.target.value)} placeholder="comma separated" className={fieldClass} />
                </label>
                <label className="block text-xs text-[#8b95a7]">
                  Privacy
                  <select value={privacy} onChange={(event) => setPrivacy(event.target.value)} className={fieldClass}>
                    <option value="public">Public</option>
                    <option value="unlisted">Unlisted</option>
                    <option value="private">Private</option>
                  </select>
                </label>
              </div>
              <label className="block text-xs text-[#8b95a7]">
                Thumbnail URL
                <input value={thumbnailUrl} onChange={(event) => setThumbnailUrl(event.target.value)} placeholder="https://…" className={fieldClass} />
              </label>
              <button
                type="submit"
                disabled={saving || !title.trim()}
                className="px-5 py-2.5 rounded-full bg-[#34d399] text-[#06211b] text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
            <SeoChecklist
              title={title}
              description={description}
              tags={tags}
              hasThumbnail={Boolean(thumbnailUrl.trim()) || video.has_custom_thumbnail}
              language={language}
              categoryId={video.category_id || ''}
              inPlaylist={Boolean(video.in_playlist)}
              privacy={privacy}
              onImprove={improveSeo}
              improving={improving}
            />
          </form>
        </>
      ) : null}
    </main>
  );
}

export default ChannelVideoDetailPage;
